import React, { forwardRef } from "react";
import { Icons } from "@/assets";
import { cx } from "@/utils/cx";

interface TextAreaFieldProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange" | "onBlur"> {
    name: string;
    label: string;
    value?: string;
    onChange?: (val: string) => void;
    onBlur?: (val: string) => void;
    error?: string;
    required?: boolean;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(({
    name,
    label,
    value,
    onChange,
    onBlur,
    error,
    required,
    disabled,
    className,
    rows = 4,
    ...props
}, ref) => {
    return (
        <div className="flex flex-col gap-2.5 w-full">
            <label htmlFor={name} className="text-base text-gray-90">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            <div
                className={cx(
                    "relative w-full rounded-[10px] p-[1.5px] transition-all duration-200 ease-in-out group",
                    error
                        ? "bg-[#ff424f]"
                        : "bg-gray-30 focus-within:bg-gradient-to-r focus-within:from-[#00B4EA] focus-within:to-[#8A00FF]",
                    disabled && "opacity-60 cursor-not-allowed bg-gray-200"
                )}
            >
                <div className="relative flex bg-white rounded-[8.5px] overflow-hidden">
                    <textarea
                        ref={ref}
                        id={name}
                        name={name}
                        value={value || ""}
                        disabled={disabled}
                        rows={rows}
                        onChange={(event) => onChange?.(event.target.value)}
                        onBlur={(event) => onBlur?.(event.target.value)}
                        className={cx(
                            "w-full min-h-[112px] px-4 py-3 text-gray-900 text-sm placeholder:text-gray-400 outline-none bg-transparent resize-y",
                            error && "pr-10",
                            disabled && "cursor-not-allowed bg-gray-50",
                            className
                        )}
                        {...props}
                    />

                    {error && (
                        <div className="absolute right-4 top-3 pointer-events-none flex items-center">
                            <Icons.ErrorIcon className="w-5 h-5 text-[#ff424f]" />
                        </div>
                    )}
                </div>
            </div>

            {error && (
                <p className="text-xs text-error-60 font-medium">{error}</p>
            )}
        </div>
    );
});

TextAreaField.displayName = "TextAreaField";
